import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const MESSAGE_ATTRIBUTE = 'Gift message';

/**
 * Keys earlier drafts of this row wrote. "Gift wrap" and "Gift" held the free opt-in before the
 * wrap became a product, and "Gift recipient" asked who the gift was for before that field was
 * dropped. The wrap is a line item now, so none of them is written any more, and every write
 * blanks all three so an older cart cannot carry them into checkout.
 */
const RETIRED_ATTRIBUTES = ['Gift wrap', 'Gift', 'Gift recipient'];

/** Everything this row can leave on the cart, blanked in one go when the wrap goes away. */
const CLEARED_ATTRIBUTES = Object.fromEntries(
  [MESSAGE_ATTRIBUTE, ...RETIRED_ATTRIBUTES].map((key) => [key, ''])
);

/**
 * Whether the row is expanded, remembered across cart re-renders.
 *
 * The snippet only marks the row open-by-default while the wrap is in the cart, so taking the
 * wrap out of an otherwise plain cart would otherwise collapse the row under the shopper
 * mid-decision. A re-render can either morph this element in place or replace it outright, so the
 * state lives beside the class rather than on an instance, and it only ever re-opens: a row the
 * shopper closed stays closed.
 */
let rowExpanded = false;

/** Re-opens every gift row on the page if the shopper had this one open. */
function restoreExpanded() {
  if (!rowExpanded) return;

  for (const row of document.querySelectorAll('gift-wrap-component')) {
    const details = row.querySelector('details');
    if (!details || details.open) continue;

    // accordion-custom re-reads these on connect, so set them rather than only `open`.
    const accordion = row.querySelector('accordion-custom');
    accordion?.setAttribute('open-by-default-on-mobile', '');
    accordion?.setAttribute('open-by-default-on-desktop', '');
    details.open = true;
  }
}

/**
 * Blanks the note if the cart no longer has wrap to write it on.
 *
 * The row clears the note itself when its own remove button takes the wrap out, but the same line
 * can also be emptied from the cart list, by its trash control or by stepping its quantity to
 * zero. Those go through cart-items-component, which knows nothing about the note, so watch the
 * cart for a wrap that has left without one and tidy up after it. The markup is already correct by
 * then, because the snippet renders no note without wrap, so this is a silent write with no
 * re-render behind it.
 */
async function clearOrphanedMessage() {
  staleCleanup = clearOrphanedMessageNow();
  return staleCleanup;
}

/**
 * The stale-note cleanup started on load, if any. Adding the wrap waits for it, so a shopper who
 * clicks add straight away cannot get back a re-render of the old note (a review cart once showed
 * "test" this way) instead of an empty one.
 * @type {Promise<void> | null}
 */
let staleCleanup = null;

async function clearOrphanedMessageNow() {
  const row = document.querySelector('gift-wrap-component');
  if (!(row instanceof HTMLElement)) return;

  const variantId = row.dataset.variantId;
  if (!variantId) return;

  try {
    const cart = await (await fetch(Theme.routes.cart_url + '.js')).json();
    const hasWrap = (cart.items ?? []).some((item) => String(item.variant_id) === String(variantId));
    if (hasWrap) return;

    if (noteDialog?.open) noteDialog.close();

    const attributes = cart.attributes ?? {};
    const stale = [MESSAGE_ATTRIBUTE, ...RETIRED_ATTRIBUTES].some((key) => attributes[key]);
    if (!stale) return;

    await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ attributes: CLEARED_ATTRIBUTES }) })
    );
  } catch (error) {}
}

/* ------------------------------------------------------------ the note modal */

/**
 * The gift note is written in a blocking modal, never inline in the cart. It opens by itself right
 * after the wrap is added, and again from "add a note" / "edit note" on the wrap line or in the gift
 * row. A blank note is allowed on purpose: "save" writes whatever is in the box, empty included,
 * and "not now" (or Escape) closes without writing, so a shopper who only wants the wrap is never
 * held up on the way to checkout.
 *
 * The markup comes from a template in the snippet. It is cloned into <body> once and opened with
 * showModal(), which puts it in the top layer above the cart drawer, and keeps it clear of the cart
 * re-renders that morph the summary and the item list underneath it.
 */

/** @type {HTMLDialogElement | null} */
let noteDialog = null;

/** Copy for the controls that open the modal, which follows whether a note has been saved. */
const noteLabel = (message) => (message ? 'edit note' : 'add a note');

/** The note as last rendered or saved. The snippet puts it on the row as `data-gift-message`. */
function currentMessage() {
  const row = document.querySelector('gift-wrap-component');
  return row instanceof HTMLElement ? row.dataset.giftMessage ?? '' : '';
}

/**
 * Puts a saved note everywhere it shows without a re-render: the label on every opener, the
 * one-line preview on the wrap line, and the row's own record of it for the next open.
 * @param {string} message
 */
function renderMessage(message) {
  for (const row of document.querySelectorAll('gift-wrap-component')) {
    if (row instanceof HTMLElement) row.dataset.giftMessage = message;
  }
  for (const label of document.querySelectorAll('[data-gift-note-label]')) {
    label.textContent = noteLabel(message);
  }
  for (const preview of document.querySelectorAll('[data-gift-note-preview]')) {
    preview.textContent = message;
  }
}

/** The modal, cloned into the page from the snippet's template the first time it is needed. */
function getNoteDialog() {
  if (noteDialog?.isConnected) return noteDialog;

  const template = document.querySelector('template[data-gift-note-template]');
  if (!(template instanceof HTMLTemplateElement)) return null;

  const dialog = template.content.querySelector('dialog')?.cloneNode(true);
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const input = dialog.querySelector('[data-gift-note-input]');
  input?.addEventListener('input', () => renderCount(dialog));
  dialog.querySelector('[data-gift-note-skip]')?.addEventListener('click', () => dialog.close());
  dialog.querySelector('[data-gift-note-save]')?.addEventListener('click', () => saveNote(dialog));

  // Escape closes like "not now". Keep it from reaching the drawer, which would close as well.
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') event.stopPropagation();
  });

  document.body.append(dialog);
  noteDialog = dialog;
  return dialog;
}

/** @param {HTMLDialogElement} dialog */
function renderCount(dialog) {
  const input = dialog.querySelector('[data-gift-note-input]');
  const counter = dialog.querySelector('[data-gift-note-count]');
  if (!(input instanceof HTMLTextAreaElement) || !counter) return;
  counter.textContent = `${input.value.length}/${input.maxLength > 0 ? input.maxLength : 200}`;
}

/**
 * Opens the note modal, prefilled with the saved note.
 * @param {string} [message] - The note to start from. Defaults to the one last saved.
 */
function openNote(message = currentMessage()) {
  const dialog = getNoteDialog();
  if (!dialog || dialog.open) return;

  const input = dialog.querySelector('[data-gift-note-input]');
  if (input instanceof HTMLTextAreaElement) input.value = message;

  const title = dialog.querySelector('[data-gift-note-title]');
  if (title) title.textContent = noteLabel(message);

  dialog.removeAttribute('aria-busy');
  renderCount(dialog);
  dialog.showModal();

  // Straight into the box with a mouse. On touch, leave the keyboard down until the shopper taps
  // the box, so the modal is read before it is half covered.
  if (input instanceof HTMLTextAreaElement && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    input.focus();
  } else {
    /** @type {HTMLElement | null} */ (dialog.querySelector('.gift-note__panel'))?.focus?.();
  }
}

/**
 * Writes the note (blank allowed) as the only attribute this row ever sets, and closes the modal.
 * @param {HTMLDialogElement} dialog
 */
async function saveNote(dialog) {
  const input = dialog.querySelector('[data-gift-note-input]');
  const message = input instanceof HTMLTextAreaElement ? input.value.trim() : '';

  dialog.setAttribute('aria-busy', 'true');
  try {
    const attributes = { [MESSAGE_ATTRIBUTE]: message };
    for (const key of RETIRED_ATTRIBUTES) attributes[key] = '';

    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ attributes }) })
    );
    if (!response.ok) throw new Error(`cart update ${response.status}`);

    renderMessage(message);
    dialog.close();
  } catch (error) {
    // Leave it open with the text intact, so the shopper can try again or choose "not now".
  } finally {
    dialog.removeAttribute('aria-busy');
  }
}

/**
 * Anything marked to open the note: the "add a note" / "edit note" controls, and the title and
 * image of the wrap line in the cart list, which would otherwise lead to the hidden product page.
 * One listener for the document, because the cart list and the drawer are re-rendered wholesale.
 */
if (!window.giftNoteOpenWatch) {
  window.giftNoteOpenWatch = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener =
      target?.closest('[data-gift-note-open]') ??
      target?.closest('[data-gift-wrap-line] .cart-items__title, [data-gift-wrap-line] .cart-items__media-container');
    if (!opener) return;

    event.preventDefault();
    openNote();
  });
}

/**
 * Cart updates from anywhere else on the page. Registered once for the document rather than per
 * instance, because a re-render can replace the element that would otherwise own the listener.
 */
if (!window.giftWrapCartWatch) {
  window.giftWrapCartWatch = true;
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    restoreExpanded();
    // Our own changes already clear the note in the same request that removes the wrap.
    const source = /** @type {any} */ (event).detail?.data?.source;
    if (source === 'gift-wrap-component') return;
    clearOrphanedMessage();
  });
}

/**
 * A custom element for the "make it a gift" cart row: a paid gift wrap the shopper adds straight
 * from the cart, and then the note the studio writes by hand on a card.
 *
 * The wrap comes first and gates the note. With no wrap in the cart the snippet renders the
 * product row and nothing to write a note in; adding the wrap opens the note modal, and the row
 * turns into a one-line confirmation with "add a note" / "edit note" and "remove". Taking the wrap
 * out drops those and blanks the stored message, so checkout can never carry a note with nothing to
 * write it on.
 *
 * The note is a cart attribute. The wrap is a real line item, added and removed through the cart
 * AJAX API against a variant pinned in the markup, so it lands in the item list with its own
 * quantity and remove control and moves the estimated total like anything else in the cart.
 */
class GiftWrap extends Component {
  connectedCallback() {
    super.connectedCallback();

    // A cart that arrived with a note but no wrap, typically one started before the wrap gated the
    // note. The markup already hides it; blank it so checkout does not receive it either.
    if (this.dataset.staleMessage === 'true') clearOrphanedMessage();

    const details = this.querySelector('details');
    if (!details) return;

    if (details.open) rowExpanded = true;
    else restoreExpanded();

    if (details.dataset.giftToggleBound) return;
    details.dataset.giftToggleBound = 'true';
    details.addEventListener('toggle', () => {
      rowExpanded = details.open;
    });
  }

  /* ---------------------------------------------------------------- the wrap */

  /**
   * Puts one gift wrap in the cart, which is what brings the note out.
   * @param {Event} event - The click event on the add button.
   */
  addWrap = (event) => this.#changeWrap(1, event);

  /**
   * Takes the gift wrap line out of the cart, and the note with it.
   * @param {Event} event - The click event on the remove button.
   */
  removeWrap = (event) => this.#changeWrap(0, event);

  /** The gift wrap quantity currently in the cart, as last rendered. */
  get #quantity() {
    return Number.parseInt(this.dataset.wrapQuantity ?? '0', 10) || 0;
  }

  /** The pinned gift wrap variant. */
  get #variantId() {
    return this.dataset.variantId ?? '';
  }

  /**
   * Sets the gift wrap line to a quantity and re-renders the cart around it.
   *
   * `/cart/add.js` cannot empty a line and takes no attributes, so adding goes through it and
   * removing through `/cart/update.js`, which empties the line and blanks the note in the same
   * request. That matters: a separate follow-up write could land after the shopper had already
   * left for checkout, and the cart would keep a note with no wrap. The wrap has no line item
   * properties, so it can only ever occupy one line and addressing it by variant is unambiguous.
   *
   * @param {number} quantity - The quantity the wrap line should end up at.
   * @param {Event} [event] - The originating event, used for performance marks.
   */
  async #changeWrap(quantity, event) {
    const variantId = this.#variantId;
    if (!variantId || quantity === this.#quantity) return;

    const removing = quantity === 0;

    if (removing) {
      if (noteDialog?.open) noteDialog.close();
    } else {
      // A leftover note is still being blanked from an earlier cart. Let it land first, so the
      // modal opens on an empty note rather than on the old one.
      await staleCleanup;
    }

    const sections = this.#sectionIds;
    const url = removing ? Theme.routes.cart_update_url : Theme.routes.cart_add_url;
    const body = removing
      ? {
          updates: { [variantId]: 0 },
          attributes: CLEARED_ATTRIBUTES,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }
      : {
          items: [{ id: variantId, quantity }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        };

    this.classList.add('gift-wrap-busy');

    try {
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();

      if (data.status) return;

      // Let the item list, the cart icon and anything else listening re-render themselves first,
      // then morph whatever they do not own, typically the summary this row lives in.
      const handled = this.#cartItemsSectionIds;
      document.dispatchEvent(
        new CartUpdateEvent({}, this.id, {
          source: 'gift-wrap-component',
          variantId,
          sections: data.sections,
        })
      );

      for (const id of sections) {
        if (handled.includes(id)) continue;
        const html = data.sections?.[id];
        if (html) morphSection(id, html);
      }

      restoreExpanded();

      // The note follows the wrap: a fresh wrap opens the modal on an empty note (blank is fine),
      // and a removed one has already had its note blanked in the same request.
      renderMessage('');
      if (!removing) openNote('');
    } catch (error) {
    } finally {
      this.classList.remove('gift-wrap-busy');
      if (event) cartPerformance.measureFromEvent('gift-wrap-change:user-action', event);
    }
  }

  /* ------------------------------------------------------------ re-rendering */

  /** Section ids that need new HTML after the wrap line changes. @returns {string[]} */
  get #sectionIds() {
    const ids = new Set(this.#cartItemsSectionIds);
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    return Array.from(ids);
  }

  /** Sections that cart-items-component re-renders on its own. @returns {string[]} */
  get #cartItemsSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter((id) => typeof id === 'string');
  }
}

if (!customElements.get('gift-wrap-component')) {
  customElements.define('gift-wrap-component', GiftWrap);
}
